import { GetServerSideProps, NextPage } from 'next';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import MessagesLight from '@/assets/messages/Messages-Light.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ErrorBoundary from '@/components/ErrorBoundary';
import ConversationsList from '@/components/messages/conversations-list/ConversationsList';
import HeadMeta from '@/components/static-pages/HeadMeta';
import Heading from '@/components/switchback/Heading/Heading';
import Loading from '@/components/switchback/Loading';
import Select from '@/components/switchback/Select';
import {
  ESelectVariants,
  IOption,
  TSelectChangeHandler,
} from '@/components/switchback/Select/Select';
import Text from '@/components/switchback/Text/Text';
import { useToast } from '@/components/switchback/Toast/ToastProvider';
import { PageEmptyState } from '@/components/ui/PageEmptyState';
import Pagination from '@/components/ui/Pagination/Pagination';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { useRouter } from '@/hooks/useRouter';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { getIsFetchingUser } from '@/redux/selectors/auth/user';
import {
  archiveConversation,
  getConversations,
  IConversation,
  PAGE_SIZE,
  TConversationsMeta,
} from '@/services/messages';

const NoConversations: React.FC = () => (
  <div className="flex flex-col items-center justify-center w-full mb-12 bg-gray-100 rounded-lg h-80 md:h-96">
    <p className="max-w-xs mb-8 font-normal text-center w-72 autoType600 mx-1/6 font-highlight md:mx-0">
      <FormattedMessage defaultMessage="You don't have any conversations." id="FrMStF" />
    </p>
  </div>
);

const EmptyState: React.FC = () => (
  <PageEmptyState
    icon={<MessagesLight className="h-auto w-[180px] lg:w-auto" />}
    title={<FormattedMessage defaultMessage="No messages yet" id="zu9kMq" />}
    description={
      <FormattedMessage
        defaultMessage="Once you connect with a host, you can view and respond to messages here."
        id="rz2zU3"
      />
    }
  />
);

interface IProps {
  initialPage: number;
  initialStatus: string;
}

const MessagesPage: React.FC<IProps> = ({ initialPage, initialStatus }) => {
  const intl = useIntl();
  const [meta, setMeta] = useState<TConversationsMeta>({});
  const [total, setTotal] = useState(0);

  const statusOptions: IOption[] = [
    {
      label: intl.formatMessage(
        { defaultMessage: 'All messages ({count})', id: '9MLuyn' },
        { count: meta.all || 0 },
      ),
      value: 'all',
    },
    {
      label: intl.formatMessage(
        { defaultMessage: 'New requests ({count})', id: 'UUPwWj' },
        { count: meta.new || 0 },
      ),
      value: 'new',
    },
    {
      label: intl.formatMessage(
        { defaultMessage: 'Approved bookings ({count})', id: 'VKoJxx' },
        { count: meta.upcoming || 0 },
      ),
      value: 'upcoming',
    },
    {
      label: intl.formatMessage(
        { defaultMessage: 'Completed ({count})', id: 'CdijBc' },
        { count: meta.completed || 0 },
      ),
      value: 'completed',
    },
    {
      label: intl.formatMessage(
        { defaultMessage: 'Archived ({count})', id: 'X005iK' },
        { count: meta.archived || 0 },
      ),
      value: 'archived',
    },
  ];

  const isFetchingUser = useSelector(getIsFetchingUser);
  const isAuthenticated = useSelector(selectAuthenticated);
  const [loading, setLoading] = useState(isAuthenticated);
  const [page, setPage] = useState(initialPage);
  const [status, setStatus] = useState<string>(
    statusOptions.find(a => a.value === initialStatus)?.value ? initialStatus : 'all',
  );
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const { addToast } = useToast();
  const router = useRouter();
  const { locale } = useLocaleSettings();
  const updateQuery = (params: URLSearchParams) => {
    router.replace({ pathname: router.asPath.split('?')[0], query: params.toString() }, undefined, {
      shallow: true,
    });
  };

  const loadPage = useCallback(async () => {
    setLoading(true);

    try {
      const [filteredConversations, totalConversations, conversationsMeta] = await getConversations(
        page,
        status,
        locale,
      );
      setConversations(filteredConversations);
      if (isNaN(totalConversations)) {
        setPage(1);
      }
      setTotal(totalConversations);
      setMeta(conversationsMeta);
    } catch {
      addToast({
        title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        description: intl.formatMessage({
          defaultMessage: 'There was an error retrieving your conversations.',
          id: 'Fyi+l0',
        }),
        type: 'TOAST_ERROR',
      });
    } finally {
      setLoading(false);
    }
  }, [page, status, locale, addToast, intl]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPage();
    }
  }, [isAuthenticated, loadPage]);

  const handleStatusChange = useCallback<TSelectChangeHandler>(
    e => {
      const newStatus = e.target.value || 'all';
      if (newStatus !== status) {
        setStatus(e.target.value || 'all');
        setPage(1);
      }
    },
    [status],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', `${page ? page : 1}`);
    params.set('status', status);
    updateQuery(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const handleClickConversation = useCallback(
    (id: number) => {
      router.push(`${router.asPath.split('?')[0]}/${id}`);
    },
    [router],
  );

  const handleArchiveConversation = useCallback(
    async (id: number) => {
      try {
        await archiveConversation(id);
        loadPage();
      } catch {
        addToast({
          title: intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
          description: intl.formatMessage({
            defaultMessage: 'There was an error archiving the conversation.',
            id: 's8eEoi',
          }),
          type: 'TOAST_ERROR',
        });
      }
    },
    [addToast, intl, loadPage],
  );

  if (!isAuthenticated && !isFetchingUser) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <EmptyState />
        </MaxWidth>
      </section>
    );
  }

  const hasSomeConversation = Boolean(meta.all || meta.archived);

  return (
    <section>
      <MaxWidth className="mx-auto my-8">
        {hasSomeConversation && (
          <div className="flex flex-row justify-between mt-12 mb-4 md:flex-row">
            <Heading level={3} className="font-medium text-gray-900 font-highlight autoType800">
              <FormattedMessage defaultMessage="Messages" id="hMzcSq" />
            </Heading>
            <div className="md:ml-auto">
              <Select
                id="messages-status-filter"
                value={status}
                options={statusOptions}
                variant={ESelectVariants.borderless}
                onChange={handleStatusChange}
              />
            </div>
          </div>
        )}

        {loading || isFetchingUser ? (
          <Loading embedded />
        ) : (
          <>
            {conversations.length < 1 ? (
              (hasSomeConversation && <NoConversations />) || <EmptyState />
            ) : (
              <>
                <ConversationsList
                  activeStatus={status}
                  conversations={conversations}
                  onConversationClick={handleClickConversation}
                  onArchiveConversation={handleArchiveConversation}
                />
                <div className="flex flex-col md:flex-row md:justify-between pb-11 lg:pt-4 lg:pb-12">
                  <Pagination
                    className="self-center w-full mb-4 md:mb-0 md:w-auto"
                    itemsPerPage={PAGE_SIZE}
                    totalItems={total}
                    stopPosition={PAGE_SIZE * page}
                    onPaginationChange={newPage => {
                      setPage(newPage);
                    }}
                    testIdPrefix="messages"
                  />
                  <Text
                    type="block"
                    className="font-bold text-center text-gray-900 autoType200 md:flex md:flex-col md:justify-center">
                    <FormattedMessage
                      defaultMessage="{start}-{stop} of {total} total results"
                      id="b5htkF"
                      description="Pagination start, stop, and total values."
                      values={{
                        start: PAGE_SIZE * (page - 1) + 1,
                        stop: PAGE_SIZE * page <= total ? PAGE_SIZE * page : total,
                        total,
                      }}
                    />
                  </Text>
                </div>
              </>
            )}
          </>
        )}
      </MaxWidth>
    </section>
  );
};

const MessagesPageWithinErrorBoundary: NextPage<IProps> = props => {
  const intl = useIntl();

  return (
    <>
      <HeadMeta
        title={intl.formatMessage({
          defaultMessage: 'Messages',
          id: 'hMzcSq',
        })}
        noindex
        nofollow
      />
      <ErrorBoundary>
        <MessagesPage {...props} />
      </ErrorBoundary>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<IProps> = async ({ query }) => {
  const { page, status } = query;
  const initialPage = typeof page === 'string' && !isNaN(Number(page)) ? Number(page) : 1;
  const initialStatus = typeof status === 'string' ? status : 'all';

  return {
    props: {
      initialPage,
      initialStatus,
    },
  };
};

export default MessagesPageWithinErrorBoundary;
